import React from 'react';
import { updateProfile } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { User, X as IconX, Edit, Loader } from 'lucide-react';
import UserAvatar from '../common/UserAvatar';

/**
 * @file Componente para o modal de perfil de usuário.
 * Permite que o usuário visualize e edite suas informações, como nome de exibição e foto de perfil.
 *
 * @param {object} props - As propriedades do componente.
 * @param {object} props.user - O objeto do usuário autenticado (de `auth.currentUser`).
 * @param {object} props.auth - A instância do serviço de autenticação do Firebase.
 * @param {object} props.db - A instância do Firestore.
 * @param {object} props.storage - A instância do Firebase Storage.
 * @param {function} props.onClose - Função para fechar o modal.
 * @param {function} props.setModalMessage - Função para exibir uma mensagem de notificação global.
 */
const UserProfileModal = ({ currentUser, targetUser, auth, db, storage, onClose, setModalMessage }) => {
    const [displayName, setDisplayName] = React.useState(targetUser.displayName || '');
    const [isUploading, setIsUploading] = React.useState(false);
    const [showConfirmDelete, setShowConfirmDelete] = React.useState(false);

    // Determina se o usuário logado está editando seu próprio perfil
    const isEditingSelf = currentUser.uid === targetUser.uid;

    const handleNameChange = async (e) => {
        e.preventDefault();
        if (displayName.trim() === targetUser.displayName) {
            setModalMessage("O nome não foi alterado.");
            return;
        }
        try {
            // A função `updateProfile` do `auth` só deve ser chamada para o usuário logado.
            if (isEditingSelf) {
                await updateProfile(auth.currentUser, { displayName: displayName.trim() });
            }
            // A atualização no Firestore pode ser feita por um administrador.
            const userDocRef = doc(db, 'users', targetUser.uid);
            await updateDoc(userDocRef, { displayName: displayName.trim() });
            setModalMessage("Nome atualizado com sucesso!");
            onClose();
        } catch (error) {
            console.error("Erro ao atualizar nome:", error);
            setModalMessage("Não foi possível atualizar o nome.");
        }
    };

    const handlePhotoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsUploading(true);
        // Mesmo prefixo de path usado no cadastro (SignUpPage), que é o único
        // liberado pelas Storage Rules (storage.rules) e pelo proxy de download do backend.
        const filePath = `profile_images/${targetUser.uid}/${file.name}`;
        const fileRef = ref(storage, filePath);

        try {
            await uploadBytes(fileRef, file);
            const photoURL = await getDownloadURL(fileRef);

            // A função `updateProfile` do `auth` só deve ser chamada para o usuário logado.
            if (isEditingSelf) {
                await updateProfile(auth.currentUser, { photoURL });
            }

            // A atualização no Firestore pode ser feita por um administrador.
            const userDocRef = doc(db, 'users', targetUser.uid);
            await updateDoc(userDocRef, { photoURL });
            setModalMessage("Foto de perfil atualizada!");
        } catch (error) {
            console.error("Erro no upload da foto:", error);
            setModalMessage("Falha ao enviar a foto.");
        } finally {
            setIsUploading(false);
        }
    };

    const handlePhotoDelete = async () => {
        if (!targetUser.photoURL) return;

        try {
            // Deriva a referência diretamente da download URL salva, em vez de
            // reconstruir o path manualmente (evita divergência com o path usado no upload).
            const fileRef = ref(storage, targetUser.photoURL);
            await deleteObject(fileRef);

            // A função `updateProfile` do `auth` só deve ser chamada para o usuário logado.
            if (isEditingSelf) {
                await updateProfile(auth.currentUser, { photoURL: '' });
            }

            // A atualização no Firestore pode ser feita por um administrador.
            const userDocRef = doc(db, 'users', targetUser.uid);
            await updateDoc(userDocRef, { photoURL: '' });
            setModalMessage("Foto de perfil removida.");
        } catch (error) {
            console.error("Erro ao remover foto:", error);
            setModalMessage("Não foi possível remover a foto.");
        } finally {
            setShowConfirmDelete(false);
        }
    };

    const handleEmailChange = () => {
        setModalMessage("Para alterar seu e-mail, por favor, entre em contato com o administrador do sistema.");
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-[1050] flex justify-center items-center p-4" onClick={onClose}>
            <div
                className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg flex flex-col"
                onClick={e => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="user-profile-title"
            >
                <header className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                    <h2 id="user-profile-title" className="text-xl font-bold flex items-center"><User className="mr-3"/>Meu Perfil</h2>
                    <button onClick={onClose} aria-label="Fechar" title="Fechar" className="p-2 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2"><IconX /></button>
                </header>
                <div className="p-6 space-y-6">
                    <div className="flex items-center gap-6">
                        <div className="relative">
                            <UserAvatar user={targetUser} size="h-24 w-24" />
                            <label
                                htmlFor="photo-upload"
                                className="absolute -bottom-1 -right-1 bg-blue-600 p-2 rounded-full cursor-pointer hover:bg-blue-700 focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2"
                                aria-label="Alterar foto de perfil"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        document.getElementById('photo-upload')?.click();
                                    }
                                }}
                            >
                                {isUploading ? <Loader className="h-4 w-4 text-white animate-spin"/> : <Edit className="h-4 w-4 text-white"/>}
                            </label>
                            <input id="photo-upload" type="file" accept="image/*" className="sr-only" onChange={handlePhotoUpload} disabled={isUploading}/>
                        </div>
                        <div>
                            <h3 className="text-2xl font-bold">{targetUser.displayName || 'Usuário'}</h3>
                            <p className="text-slate-500">{targetUser.email}</p>
                            {targetUser.photoURL && !showConfirmDelete && <button onClick={() => setShowConfirmDelete(true)} className="text-xs text-red-500 hover:underline mt-1">Remover foto</button>}
                            {showConfirmDelete && (
                                <div className="mt-2 flex items-center gap-2">
                                    <button onClick={handlePhotoDelete} className="text-xs btn-admin bg-red-500 hover:bg-red-600">Confirmar</button>
                                    <button onClick={() => setShowConfirmDelete(false)} className="text-xs btn-admin bg-slate-200 text-slate-800 hover:bg-slate-300">Cancelar</button>
                                </div>
                            )}
                        </div>
                    </div>
                    <form onSubmit={handleNameChange} className="space-y-4">
                        <div>
                            <label htmlFor="displayName" className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Nome de Exibição</label>
                            <input id="displayName" type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="input-style px-4 w-full" />
                        </div>
                         <div>
                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">E-mail</label>
                            <div className="flex items-center gap-2">
                                <input type="email" value={targetUser.email} disabled className="input-style px-4 w-full flex-grow" />
                                <button type="button" onClick={handleEmailChange} className="text-sm font-medium text-blue-600 hover:underline">Alterar</button>
                            </div>
                        </div>
                        <div className="flex justify-end gap-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                            <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
                            <button type="submit" className="btn-primary">Salvar Alterações</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default UserProfileModal;
